import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { EventSearchItem, CompetitionSearchFilters, Country } from '../types/api';
import { apiService } from '../services/api';
import { urlParams } from '../utils/urlParams';
import { getCircleFlagUrl, resolveCountryName } from '../utils/countryFlag';
import { AppHeader } from './AppHeader';
import { RangeSlider } from './RangeSlider';
import { Search, X, MapPin, Calendar, Ruler, Layers, Loader2 } from 'lucide-react';

const PAGE_SIZE = 24;

interface EventEntry {
  competitionId: string;
  event: EventSearchItem;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

const EventCard: React.FC<{
  entry: EventEntry;
  countries: Country[];
  onClick: () => void;
}> = ({ entry, countries, onClick }) => {
  const { event } = entry;
  const flagUrl = event.unified_location
    ? getCircleFlagUrl(event.unified_location)
    : null;
  const countryName = event.unified_location
    ? resolveCountryName(event.unified_location, countries)
    : null;

  return (
    <button
      onClick={onClick}
      className="w-full bg-[#E7EEE3] rounded-[28px] shadow-md p-4 sm:p-5 pl-5 sm:pl-7 text-left transition-shadow hover:shadow-lg relative overflow-visible ml-4 sm:ml-6"
    >
      {/* Flag avatar (pop-out) */}
      <div className="absolute -left-12 sm:-left-14 top-1/2 -translate-y-1/2 h-16 w-16 rounded-full bg-white border-[3.5px] border-white flex items-center justify-center overflow-hidden shadow-md">
        {flagUrl ? (
          <img
            src={flagUrl}
            alt={countryName || ''}
            className="w-full h-full rounded-full object-cover"
            onError={(e) => { (e.target as HTMLImageElement).src = '/icons/flags_3180347.png'; }}
          />
        ) : (
          <img src="/icons/flags_3180347.png" alt="" className="w-8 h-8 object-contain opacity-60" />
        )}
      </div>

      <div className="min-w-0">
        <h3 className="text-[15px] font-bold text-[#132B64] leading-tight truncate">
          {event.unified_competition_name || 'Unnamed Event'}
        </h3>

        {event.unified_event_code && (
          <span className="inline-block mt-1 px-2 py-0.5 bg-[#132B64]/10 text-[#132B64] text-[11px] font-semibold rounded-full">
            {event.unified_event_code}
          </span>
        )}

        <div className="mt-2 space-y-1.5">
          {event.unified_competition_date && (
            <div className="flex items-center gap-1.5 text-xs text-gray-600">
              <Calendar size={13} className="shrink-0" />
              <span>{formatDate(event.unified_competition_date)}</span>
            </div>
          )}
          {countryName && (
            <div className="flex items-center gap-1.5 text-xs text-gray-600">
              <MapPin size={13} className="shrink-0" />
              <span>{countryName}</span>
            </div>
          )}
          {event.class_ids.length > 0 && (
            <div className="flex items-center gap-1.5 text-xs text-gray-600">
              <Layers size={13} className="shrink-0" />
              <span>{event.class_ids.length} {event.class_ids.length === 1 ? 'class' : 'classes'}</span>
            </div>
          )}
        </div>
      </div>
    </button>
  );
};

export const CompetitionsSearch: React.FC = () => {
  const [events, setEvents] = useState<EventEntry[]>([]);
  const [totalEvents, setTotalEvents] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [filters, setFilters] = useState<CompetitionSearchFilters>(() => urlParams.getCompetitionFiltersFromUrl());
  const [countries, setCountries] = useState<Country[]>([]);
  const [showSearchDialog, setShowSearchDialog] = useState(false);
  const [dialogFilters, setDialogFilters] = useState<CompetitionSearchFilters>({});
  const [latestEvents, setLatestEvents] = useState<EventEntry[]>([]);
  const [latestTotal, setLatestTotal] = useState<number | null>(null);
  const [latestLoading, setLatestLoading] = useState(false);
  const [latestLoadingMore, setLatestLoadingMore] = useState(false);
  const [nameSuggestions, setNameSuggestions] = useState<string[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [compNameSuggestions, setCompNameSuggestions] = useState<string[]>([]);
  const [loadingCompNameSuggestions, setLoadingCompNameSuggestions] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');

  const nameDebounceRef = useRef<number | undefined>(undefined);
  const compNameDebounceRef = useRef<number | undefined>(undefined);
  const loadIdRef = useRef(0);
  const latestLoadIdRef = useRef(0);

  const filteredCountries = useMemo(() => {
    if (!countrySearch) return countries;
    const lower = countrySearch.toLowerCase();
    return countries.filter(c => c.name.toLowerCase().includes(lower));
  }, [countries, countrySearch]);

  useEffect(() => {
    apiService.getCountries().then(setCountries).catch(() => {});
  }, []);

  useEffect(() => {
    const term = dialogFilters.competition_name?.trim() || '';
    if (compNameDebounceRef.current) window.clearTimeout(compNameDebounceRef.current);
    if (term.length < 2) {
      setCompNameSuggestions([]);
      return;
    }
    compNameDebounceRef.current = window.setTimeout(async () => {
      setLoadingCompNameSuggestions(true);
      try {
        const res = await apiService.searchCompetitions({ competition_name: term }, 0, 6);
        const names = Array.from(
          new Set(
            Object.values(res.events)
              .map(ev => ev.unified_competition_name)
              .filter(Boolean) as string[]
          )
        );
        setCompNameSuggestions(names.slice(0, 6));
      } catch {
        setCompNameSuggestions([]);
      } finally {
        setLoadingCompNameSuggestions(false);
      }
    }, 250);
    return () => { if (compNameDebounceRef.current) window.clearTimeout(compNameDebounceRef.current); };
  }, [dialogFilters.competition_name]);

  useEffect(() => {
    setNameSuggestions([]);
  }, [dialogFilters.name]);

  const hasFilters = Object.keys(filters).length > 0;

  const eventsFromResponse = useCallback((eventsDict: Record<string, EventSearchItem>): EventEntry[] => {
    return Object.entries(eventsDict).map(([id, ev]) => ({ competitionId: id, event: ev }));
  }, []);

  const loadCompetitions = useCallback(async (searchFilters: CompetitionSearchFilters, skip = 0, append = false) => {
    const id = ++loadIdRef.current;
    if (!append) setLoading(true);
    else setLoadingMore(true);
    setError(undefined);

    try {
      const res = await apiService.searchCompetitions(searchFilters, skip, PAGE_SIZE);
      if (id !== loadIdRef.current) return;
      const newEntries = eventsFromResponse(res.events);
      if (append) {
        setEvents(prev => [...prev, ...newEntries]);
      } else {
        setEvents(newEntries);
      }
      setTotalEvents(res.total);
    } catch (err) {
      if (id !== loadIdRef.current) return;
      setError(err instanceof Error ? err.message : 'Failed to load competitions');
    } finally {
      if (id === loadIdRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [eventsFromResponse]);

  const loadLatestCompetitions = useCallback(async (skip = 0, append = false) => {
    const id = ++latestLoadIdRef.current;
    if (!append) setLatestLoading(true);
    else setLatestLoadingMore(true);
    try {
      const res = await apiService.searchCompetitions({}, skip, PAGE_SIZE);
      if (id !== latestLoadIdRef.current) return;
      const newEntries = eventsFromResponse(res.events);
      if (append) {
        setLatestEvents(prev => [...prev, ...newEntries]);
      } else {
        setLatestEvents(newEntries);
      }
      setLatestTotal(res.total);
    } catch {
      // silently fail for latest
    } finally {
      if (id === latestLoadIdRef.current) {
        setLatestLoading(false);
        setLatestLoadingMore(false);
      }
    }
  }, [eventsFromResponse]);

  useEffect(() => {
    if (hasFilters) {
      loadCompetitions(filters);
    } else {
      loadLatestCompetitions();
    }
  }, [filters, hasFilters, loadCompetitions, loadLatestCompetitions]);

  useEffect(() => {
    const handleUrlChange = () => {
      const viewMode = urlParams.getViewModeFromUrl();
      if (viewMode !== 'competitions') return;
      const urlFilters = urlParams.getCompetitionFiltersFromUrl();
      setFilters(urlFilters);
    };
    window.addEventListener('popstate', handleUrlChange);
    window.addEventListener('urlchange', handleUrlChange);
    return () => {
      window.removeEventListener('popstate', handleUrlChange);
      window.removeEventListener('urlchange', handleUrlChange);
    };
  }, []);

  const handleSearchApply = useCallback((newFilters: CompetitionSearchFilters) => {
    setShowSearchDialog(false);
    urlParams.updateUrlWithCompetitionFilters(newFilters);
  }, []);

  const handleLoadMore = useCallback(() => {
    if (loadingMore) return;
    loadCompetitions(filters, events.length, true);
  }, [filters, events.length, loadingMore, loadCompetitions]);

  const handleLoadMoreLatest = useCallback(() => {
    if (latestLoadingMore) return;
    loadLatestCompetitions(latestEvents.length, true);
  }, [latestEvents.length, latestLoadingMore, loadLatestCompetitions]);

  const handleClearFilters = useCallback(() => {
    setEvents([]);
    setTotalEvents(null);
    urlParams.navigateToCompetitions();
  }, []);

  const hasMore = totalEvents !== null && events.length < totalEvents;

  const filterScrollRef = useRef<HTMLDivElement>(null);
  const [showScrollHint, setShowScrollHint] = useState(false);

  const checkFilterScroll = useCallback(() => {
    const el = filterScrollRef.current;
    if (!el) return;
    const canScroll = el.scrollHeight > el.clientHeight + 4;
    const notAtBottom = el.scrollTop + el.clientHeight < el.scrollHeight - 8;
    setShowScrollHint(canScroll && notAtBottom);
  }, []);

  useEffect(() => {
    if (!showSearchDialog) return;
    const el = filterScrollRef.current;
    if (!el) return;
    const raf = requestAnimationFrame(checkFilterScroll);
    el.addEventListener('scroll', checkFilterScroll, { passive: true });
    const ro = new ResizeObserver(checkFilterScroll);
    ro.observe(el);
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener('scroll', checkFilterScroll);
      ro.disconnect();
    };
  }, [showSearchDialog, checkFilterScroll]);

  useEffect(() => {
    if (!showSearchDialog) return;
    const orig = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = orig; };
  }, [showSearchDialog]);

  return (
    <div className="min-h-screen bg-white pb-16 lg:pb-0">
      <AppHeader onLogoClick={() => urlParams.resetToHome()} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search button */}
        <div className="mb-8 space-y-4">
          <div className="flex flex-col items-center justify-center gap-3">
            <button
              onClick={() => {
                setDialogFilters(filters);
                setShowSearchDialog(true);
              }}
              className="inline-flex items-center justify-center gap-3 w-80 px-8 py-5 text-base font-semibold text-[#132B64] bg-white border-2 border-[#132B64] rounded-full hover:bg-gray-50 transition-colors"
            >
              <img src="/icons/search_icon.svg" alt="" className="h-6 w-6" />
              Search Competitions
            </button>
          </div>
        </div>

        {/* Filter chips */}
        {hasFilters && (
          <div className="mb-6 flex justify-center">
            <div className="w-full max-w-[320px] lg:max-w-[500px]">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-700">
                  Active Filters ({[filters.competition_name, filters.name, filters.location, (filters.height_cm_min != null || filters.height_cm_max != null) ? true : undefined, (filters.date_from || filters.date_to) ? true : undefined, filters.is_pony_class !== undefined ? true : undefined].filter(Boolean).length})
                </h3>
                <button onClick={handleClearFilters} className="text-sm text-gray-500 hover:text-gray-700 underline">
                  Clear all
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {filters.competition_name && (
                  <div className="inline-flex items-center bg-blue-100 text-blue-800 text-sm px-3 py-1 rounded-full">
                    <span>Event: {filters.competition_name}</span>
                    <button onClick={() => { const { competition_name, ...rest } = filters; urlParams.updateUrlWithCompetitionFilters(rest); }} className="ml-2 text-blue-600 hover:text-blue-800"><X size={14} /></button>
                  </div>
                )}
                {filters.name && (
                  <div className="inline-flex items-center bg-blue-100 text-blue-800 text-sm px-3 py-1 rounded-full">
                    <span>Class: {filters.name}</span>
                    <button onClick={() => { const { name, ...rest } = filters; urlParams.updateUrlWithCompetitionFilters(rest); }} className="ml-2 text-blue-600 hover:text-blue-800"><X size={14} /></button>
                  </div>
                )}
                {filters.location && (
                  <div className="inline-flex items-center bg-blue-100 text-blue-800 text-sm px-3 py-1 rounded-full">
                    <span>Location: {resolveCountryName(filters.location, countries)}</span>
                    <button onClick={() => { const { location, ...rest } = filters; urlParams.updateUrlWithCompetitionFilters(rest); }} className="ml-2 text-blue-600 hover:text-blue-800"><X size={14} /></button>
                  </div>
                )}
                {(filters.height_cm_min != null || filters.height_cm_max != null) && (
                  <div className="inline-flex items-center bg-blue-100 text-blue-800 text-sm px-3 py-1 rounded-full">
                    <span>Height: {filters.height_cm_min ?? '?'}–{filters.height_cm_max ?? '?'} cm</span>
                    <button onClick={() => { const { height_cm_min, height_cm_max, ...rest } = filters; urlParams.updateUrlWithCompetitionFilters(rest); }} className="ml-2 text-blue-600 hover:text-blue-800"><X size={14} /></button>
                  </div>
                )}
                {(filters.date_from || filters.date_to) && (
                  <div className="inline-flex items-center bg-blue-100 text-blue-800 text-sm px-3 py-1 rounded-full">
                    <span>Date: {filters.date_from ?? '…'} – {filters.date_to ?? '…'}</span>
                    <button onClick={() => { const { date_from, date_to, ...rest } = filters; urlParams.updateUrlWithCompetitionFilters(rest); }} className="ml-2 text-blue-600 hover:text-blue-800"><X size={14} /></button>
                  </div>
                )}
                {filters.is_pony_class !== undefined && (
                  <div className="inline-flex items-center bg-blue-100 text-blue-800 text-sm px-3 py-1 rounded-full">
                    <span>{filters.is_pony_class ? 'Pony classes' : 'Non-pony classes'}</span>
                    <button onClick={() => { const { is_pony_class, ...rest } = filters; urlParams.updateUrlWithCompetitionFilters(rest); }} className="ml-2 text-blue-600 hover:text-blue-800"><X size={14} /></button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Search Results */}
        {hasFilters && (
          <div className="flex justify-center">
            <div className="w-full max-w-[320px] lg:max-w-[500px]">
              {!loading && totalEvents !== null && (
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  {totalEvents === 0
                    ? 'No events found'
                    : `${events.length.toLocaleString()} of ${totalEvents.toLocaleString()} events`}
                </h2>
              )}

              {loading && (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#132B64]" />
                </div>
              )}

              {error && (
                <div className="text-center py-8 text-red-600">{error}</div>
              )}

              {!loading && !error && (
                <div className="grid grid-cols-1 gap-4">
                  {events.map((entry) => (
                    <EventCard
                      key={entry.competitionId}
                      entry={entry}
                      countries={countries}
                      onClick={() => {
                        urlParams.navigateToEventDetail(entry.competitionId);
                      }}
                    />
                  ))}
                </div>
              )}

              {hasMore && !loading && (
                <div className="mt-6 flex justify-center">
                  <button
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    className="px-6 py-2 text-sm font-medium text-[#132B64] bg-gray-100 rounded-full hover:bg-gray-200 transition-colors disabled:opacity-50"
                  >
                    {loadingMore ? 'Loading...' : 'Show more'}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Latest Events */}
        {!hasFilters && (
          <div className="flex justify-center">
            <div className="w-full max-w-[320px] lg:max-w-[500px]">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Latest Events</h2>

              {latestLoading && (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#132B64]" />
                </div>
              )}

              {!latestLoading && latestEvents.length > 0 && (
                <div className="grid grid-cols-1 gap-4">
                  {latestEvents.map((entry) => (
                    <EventCard
                      key={entry.competitionId}
                      entry={entry}
                      countries={countries}
                      onClick={() => {
                        urlParams.navigateToEventDetail(entry.competitionId);
                      }}
                    />
                  ))}
                </div>
              )}

              {!latestLoading && latestTotal !== null && latestEvents.length < latestTotal && (
                <div className="mt-6 flex justify-center">
                  <button
                    onClick={handleLoadMoreLatest}
                    disabled={latestLoadingMore}
                    className="px-6 py-2 text-sm font-medium text-[#132B64] bg-gray-100 rounded-full hover:bg-gray-200 transition-colors disabled:opacity-50"
                  >
                    {latestLoadingMore ? 'Loading...' : 'Show more'}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Search dialog */}
      {showSearchDialog && (
        <div className="fixed top-16 lg:top-0 left-0 lg:left-60 right-0 bottom-16 lg:bottom-0 bg-black/35 lg:bg-transparent flex items-center justify-center z-50 px-4 py-4 sm:py-6">
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[calc(100vh-10rem)] flex flex-col relative overflow-hidden">
            {/* Header */}
            <div className="p-6 sm:p-7 pb-0 flex items-start justify-between gap-4 shrink-0">
              <h3 className="text-2xl font-semibold text-[#132B64]">Search Competitions</h3>
              <button
                onClick={() => setShowSearchDialog(false)}
                className="inline-flex items-center justify-center h-10 w-10 rounded-full hover:bg-gray-100 text-gray-700 shrink-0"
                aria-label="Close"
              >
                <X size={24} strokeWidth={2.5} />
              </button>
            </div>

            {/* Scrollable filter form */}
            <div ref={filterScrollRef} className="flex-1 min-h-0 overflow-y-auto p-6 sm:p-7 pt-4 relative">
              <div className="space-y-5">
                {/* Competition/event name with autocomplete */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Competition Name</label>
                  <div className="relative">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={dialogFilters.competition_name || ''}
                      onChange={(e) => setDialogFilters(f => ({ ...f, competition_name: e.target.value || undefined }))}
                      placeholder="e.g. CHIO Aachen"
                      className="w-full pl-9 pr-8 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-[#132B64] focus:border-transparent outline-none"
                      autoComplete="off"
                    />
                    {loadingCompNameSuggestions && (
                      <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 animate-spin" />
                    )}
                  </div>
                  {compNameSuggestions.length > 0 && (
                    <div className="mt-1 border border-gray-200 rounded-xl bg-white shadow-sm divide-y max-h-56 overflow-auto">
                      {compNameSuggestions.map((suggestion) => (
                        <button
                          key={suggestion}
                          type="button"
                          onClick={() => {
                            setDialogFilters(f => ({ ...f, competition_name: suggestion }));
                            setCompNameSuggestions([]);
                          }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Class name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Class Name</label>
                  <div className="relative">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={dialogFilters.name || ''}
                      onChange={(e) => setDialogFilters(f => ({ ...f, name: e.target.value || undefined }))}
                      placeholder="e.g. Grand Prix"
                      className="w-full pl-9 pr-8 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-[#132B64] focus:border-transparent outline-none"
                      autoComplete="off"
                    />
                  </div>
                </div>

                {/* Country dropdown */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                  <div className="relative">
                    <details className="group" onToggle={() => setCountrySearch('')}>
                      <summary className="flex items-center justify-between px-3 py-2.5 border border-gray-300 rounded-xl bg-white text-sm text-gray-700 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#132B64] select-none">
                        <span className="flex items-center gap-2">
                          <MapPin size={16} className="text-gray-400 shrink-0" />
                          {dialogFilters.location
                            ? resolveCountryName(dialogFilters.location, countries)
                            : 'Select country'}
                        </span>
                        <span className="text-gray-400 group-open:rotate-180 transition-transform">⌄</span>
                      </summary>
                      <div className="absolute z-20 mt-1 w-full rounded-xl border border-gray-200 bg-white shadow-lg">
                        <div className="p-2 border-b border-gray-100">
                          <input
                            type="text"
                            value={countrySearch}
                            onChange={e => setCountrySearch(e.target.value)}
                            placeholder="Search countries..."
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#132B64]"
                            onClick={e => e.stopPropagation()}
                          />
                        </div>
                        <div className="max-h-56 overflow-auto">
                          {dialogFilters.location && (
                            <button
                              type="button"
                              onClick={(e) => {
                                setDialogFilters(f => { const { location, ...rest } = f; return rest; });
                                (e.target as HTMLElement).closest('details')?.removeAttribute('open');
                              }}
                              className="w-full text-left px-3 py-2 text-sm text-gray-400 hover:bg-gray-50 italic"
                            >
                              Clear selection
                            </button>
                          )}
                          {filteredCountries.map((c) => {
                            const selected = dialogFilters.location === c.code;
                            return (
                              <button
                                key={c.code}
                                type="button"
                                onClick={(e) => {
                                  setDialogFilters(f => ({ ...f, location: c.code }));
                                  (e.target as HTMLElement).closest('details')?.removeAttribute('open');
                                }}
                                className={`w-full flex items-center px-3 py-2 text-sm hover:bg-gray-50 ${selected ? 'bg-blue-50 font-medium' : ''}`}
                              >
                                {c.name}
                              </button>
                            );
                          })}
                          {filteredCountries.length === 0 && (
                            <p className="px-3 py-2 text-sm text-gray-400">No countries found</p>
                          )}
                        </div>
                      </div>
                    </details>
                  </div>
                </div>

                {/* Height range */}
                <RangeSlider
                  label="Obstacle Height (cm)"
                  valueMin={dialogFilters.height_cm_min}
                  valueMax={dialogFilters.height_cm_max}
                  minLimit={100}
                  maxLimit={165}
                  step={5}
                  unit="cm"
                  onChange={(min, max) => {
                    setDialogFilters(f => ({
                      ...f,
                      height_cm_min: min,
                      height_cm_max: max,
                    }));
                  }}
                />

                {/* Pony class filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Class Type</label>
                  <select
                    value={dialogFilters.is_pony_class === true ? 'true' : dialogFilters.is_pony_class === false ? 'false' : ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      setDialogFilters(f => ({
                        ...f,
                        is_pony_class: val === '' ? undefined : val === 'true',
                      }));
                    }}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-[#132B64] focus:border-transparent outline-none"
                  >
                    <option value="">All classes</option>
                    <option value="true">Pony classes only</option>
                    <option value="false">Non-pony classes only</option>
                  </select>
                </div>

                {/* Date range */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                      value={dialogFilters.date_from || ''}
                      onChange={(e) => setDialogFilters(f => ({ ...f, date_from: e.target.value || undefined }))}
                      className="w-full min-w-0 px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-[#132B64] focus:border-transparent outline-none"
                    />
                    <span className="text-gray-400 shrink-0">–</span>
                    <input
                      type="date"
                      value={dialogFilters.date_to || ''}
                      onChange={(e) => setDialogFilters(f => ({ ...f, date_to: e.target.value || undefined }))}
                      className="w-full min-w-0 px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-[#132B64] focus:border-transparent outline-none"
                    />
                  </div>
                </div>
              </div>

              {showScrollHint && (
                <div className="sticky bottom-0 left-0 right-0 pointer-events-none" aria-hidden="true">
                  <div className="h-10 bg-gradient-to-t from-white to-transparent" />
                  <div className="bg-white flex justify-center pb-1">
                    <span className="text-xs text-gray-400">Scroll for more filters</span>
                  </div>
                </div>
              )}
            </div>

            {/* Sticky footer */}
            <div className="p-6 sm:p-7 pt-4 border-t border-gray-100 shrink-0 flex justify-center">
              <button
                onClick={() => {
                  const cleaned: CompetitionSearchFilters = {};
                  if (dialogFilters.competition_name) cleaned.competition_name = dialogFilters.competition_name;
                  if (dialogFilters.name) cleaned.name = dialogFilters.name;
                  if (dialogFilters.location) cleaned.location = dialogFilters.location;
                  if (dialogFilters.height_cm_min !== undefined) cleaned.height_cm_min = dialogFilters.height_cm_min;
                  if (dialogFilters.height_cm_max !== undefined) cleaned.height_cm_max = dialogFilters.height_cm_max;
                  if (dialogFilters.date_from) cleaned.date_from = dialogFilters.date_from;
                  if (dialogFilters.date_to) cleaned.date_to = dialogFilters.date_to;
                  if (dialogFilters.is_pony_class !== undefined) cleaned.is_pony_class = dialogFilters.is_pony_class;
                  handleSearchApply(cleaned);
                }}
                className="px-8 py-3 text-base font-semibold text-white bg-[#132B64] rounded-full hover:bg-[#0f2350] transition-colors"
              >
                Find
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
